import { useMemo } from "react";
import { groupBy } from "lodash";
import type { ComponentMeta } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { isPropsMetaStyles } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { StyleMetaAttributes } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import type { EditorFieldProps } from "./DefaultEditorFields";

export interface CategoryMetadata {
  name: string;
  nameI18n?: string;
}

export interface CategorizedField {
  categories: CategoryMetadata[] | ReadonlyArray<CategoryMetadata>;
  name: string;
  propMeta: PropsMeta | StyleMetaAttributes;
}

const OTHER_CATEGORY_NAME = "other";

export type TabGroups = Record<string, Record<string, EditorFieldProps[]>>;
export type TabOnlyGroups = Record<string, EditorFieldProps[]>;

const extractProps = (
  namePrefix: string,
  propMeta: PropsMeta | StyleMetaAttributes,
): CategorizedField[] => {
  if (!isPropsMetaStyles(propMeta)) {
    return [
      {
        name: namePrefix,
        propMeta,
        categories: propMeta.category || [],
      },
    ];
  }

  return Object.entries(propMeta.attributes).flatMap(
    ([key, styleAttributeMeta]) => {
      const fullPath = `${namePrefix}.${key}`;
      return extractProps(fullPath, styleAttributeMeta);
    },
  );
};

const categorizeProps = (meta: ComponentMeta): CategorizedField[] =>
  Object.entries(meta.props ?? {})
    .filter(([, propMeta]) => propMeta.prop !== "children")
    .flatMap(([name, propMeta]) => extractProps(name, propMeta));

const groupPropsByCategories = (propsArray: CategorizedField[]) => {
  const twoLevelProps = propsArray.filter(prop => prop.categories.length === 2);
  const oneLevelProps = propsArray.filter(prop => prop.categories.length === 1);
  const unCategorizedProps = propsArray.filter(
    prop => prop.categories.length === 0,
  );

  return { twoLevelProps, oneLevelProps, unCategorizedProps };
};

const getTabCategories = (
  twoLevelProps: CategorizedField[],
  oneLevelProps: CategorizedField[],
): string[] => [
  ...new Set([
    ...oneLevelProps.map(prop => prop.categories[0].name),
    ...twoLevelProps.map(prop => prop.categories[0].name),
  ]),
];

const buildNestedGroups = (
  tabCategories: string[],
  twoLevelProps: CategorizedField[],
): TabGroups => {
  const nestedGroups: TabGroups = {};

  for (const tabCategory of tabCategories) {
    const tabProps = twoLevelProps.filter(
      prop => prop.categories[0].name === tabCategory,
    );
    const groupedBySecondary = groupBy(
      tabProps,
      prop => prop.categories[1].name,
    );

    if (Object.keys(groupedBySecondary).length > 0) {
      nestedGroups[tabCategory] = Object.fromEntries(
        Object.entries(groupedBySecondary).map(([key, value]) => [
          key,
          value.map(v => ({ name: v.name, propMeta: v.propMeta })),
        ]),
      );
    }
  }

  return nestedGroups;
};

const buildTabOnlyGroups = (
  tabCategories: string[],
  oneLevelProps: CategorizedField[],
): TabOnlyGroups => {
  const tabOnlyGroups: TabOnlyGroups = {};

  for (const tabCategory of tabCategories) {
    const categoryProps = oneLevelProps.filter(
      prop => prop.categories[0].name === tabCategory,
    );
    if (categoryProps.length > 0) {
      tabOnlyGroups[tabCategory] = categoryProps.map(({ name, propMeta }) => ({
        name,
        propMeta,
      }));
    }
  }

  return tabOnlyGroups;
};

interface HandleUnCategorizedPropsResult {
  tabCategories: ReadonlyArray<string>;
  tabOnlyGroups: TabOnlyGroups;
  unCategorizedProps: EditorFieldProps[];
}

const handleUnCategorizedProps = (
  unCategorizedProps: CategorizedField[],
  tabCategories: ReadonlyArray<string>,
  tabOnlyGroups: TabOnlyGroups,
): HandleUnCategorizedPropsResult => {
  const props = unCategorizedProps.map(({ name, propMeta }) => ({
    name,
    propMeta,
  }));

  if (unCategorizedProps.length === 0) {
    return { tabCategories, tabOnlyGroups, unCategorizedProps: [] };
  }

  if (tabCategories.length > 0) {
    const newTabOnlyGroups = { ...tabOnlyGroups };
    let newTabCategories = [...tabCategories];

    if (newTabCategories.includes(OTHER_CATEGORY_NAME)) {
      newTabOnlyGroups[OTHER_CATEGORY_NAME] = [
        ...(newTabOnlyGroups[OTHER_CATEGORY_NAME] || []),
        ...props,
      ];
    } else {
      newTabCategories = [...newTabCategories, OTHER_CATEGORY_NAME];
      newTabOnlyGroups[OTHER_CATEGORY_NAME] = props;
    }

    return {
      tabCategories: newTabCategories,
      tabOnlyGroups: newTabOnlyGroups,
      unCategorizedProps: [],
    };
  }

  return { tabCategories, tabOnlyGroups, unCategorizedProps: props };
};

export const useCategorizedBlockProps = (meta: ComponentMeta) =>
  useMemo(() => {
    const propsArray = categorizeProps(meta);

    const { twoLevelProps, oneLevelProps, unCategorizedProps } =
      groupPropsByCategories(propsArray);

    const initialTabCategories = getTabCategories(twoLevelProps, oneLevelProps);

    const nestedGroups = buildNestedGroups(initialTabCategories, twoLevelProps);

    const initialTabOnlyGroups = buildTabOnlyGroups(
      initialTabCategories,
      oneLevelProps,
    );

    const {
      tabCategories,
      tabOnlyGroups,
      unCategorizedProps: finalUncategorized,
    } = handleUnCategorizedProps(
      unCategorizedProps,
      initialTabCategories,
      initialTabOnlyGroups,
    );

    return {
      nestedGroups,
      tabOnlyGroups,
      unCategorizedProps: finalUncategorized,
      tabCategories,
    };
  }, [meta]);
